
import React, { useState } from 'react';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Shield, Users, Calendar, Settings, RefreshCw, Edit } from 'lucide-react';
import ChangePasswordForm from '@/components/auth/ChangePasswordForm';
import UpdatePinForm from '@/components/auth/UpdatePinForm';
import SupportProfileForm from '@/components/profile/SupportProfileForm';

const InternalUserProfile: React.FC = () => {
  const { user, refreshUser } = useInternalAuth();
  const [activeForm, setActiveForm] = useState<'password' | 'pin' | 'profile' | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshUser();
    } catch (error) {
      console.error('Error refreshing user data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  if (!user) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <p className="text-gray-400 text-center">
          No internal user data available.
        </p>
      </div>
    );
  }

  if (activeForm === 'password') {
    return (
      <ChangePasswordForm 
        onBack={() => setActiveForm(null)} 
        isInternal={true}
      />
    );
  }

  if (activeForm === 'pin') {
    return (
      <UpdatePinForm onBack={() => setActiveForm(null)} />
    );
  }

  if (activeForm === 'profile') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Edit Profile</h2>
          <Button
            onClick={() => setActiveForm(null)}
            variant="outline"
            className="border-gray-600 text-gray-400 hover:bg-gray-700"
          >
            Back to Account Overview
          </Button>
        </div>
        <SupportProfileForm />
      </div>
    );
  }

  const getRoleDisplayName = (role: string) => {
    const roleMap = {
      'player': 'Player',
      'coach': 'Coach',
      'team-owner': 'Team Owner',
      'venue': 'Venue Manager',
      'creative': 'Creative',
      'support': 'Support/Score Keeper',
      'fan': 'Fan',
      'parent': 'Parent',
      'admin': 'Administrator'
    };
    return roleMap[role] || role;
  };

  const getAgeGroupDisplayName = (ageGroup: string) => {
    const ageGroupMap = {
      '1': 'Age 1', '2': 'Age 2', '3': 'Age 3', '4': 'Age 4', '5': 'Age 5',
      '6': 'Age 6', '7': 'Age 7', '8': 'Age 8', '9': 'Age 9', '10': 'Age 10',
      '11': 'Age 11', '12': 'Age 12', '13': 'Age 13',
      '13-and-under': '13 and Under',
      '14-17': '14-17 Years',
      '18-plus': '18+ Years'
    };
    return ageGroupMap[ageGroup] || ageGroup;
  };

  return (
    <div className="space-y-6">
      {/* Account Overview */}
      <Card className="bg-gray-900/50 border border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                <User className="h-6 w-6" />
                Account Information
              </CardTitle>
              <p className="text-gray-400">
                Your New School account details and settings
              </p>
            </div>
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              size="sm"
              className="border-gray-600 text-gray-400 hover:bg-gray-700"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-400">Username</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <span className="text-white">{user.username}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">Account Role</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-sports-green" />
                  <span className="text-white">{getRoleDisplayName(user.role)}</span>
                  {user.role === 'admin' && (
                    <span className="ml-2 px-2 py-1 bg-red-600/20 text-red-400 text-xs rounded-full border border-red-600/30">
                      ADMIN
                    </span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">Age Group</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700 flex items-center gap-2">
                  <Users className="h-4 w-4 text-sports-green" />
                  <span className="text-white">{getAgeGroupDisplayName(user.age_group)}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-400">Account Created</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-sports-green" />
                  <span className="text-white">
                    {new Date(user.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">Last Login</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <span className="text-white">
                    {user.last_login 
                      ? new Date(user.last_login).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Today'
                    }
                  </span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-400">Account Status</label>
                <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    user.is_active 
                      ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                      : 'bg-red-600/20 text-red-400 border border-red-600/30'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin Features */}
      {user.role === 'admin' && (
        <Card className="bg-red-900/20 border border-red-500/30">
          <CardHeader>
            <CardTitle className="text-xl text-red-400 flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Administrator Access
            </CardTitle>
            <p className="text-gray-400">
              You have full administrative privileges on this system
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <h4 className="text-red-400 font-medium mb-2">🛡️ Administrative Capabilities</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">System Testing & Debugging</span>
                  <span className="text-green-400">✓ Enabled</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">User Management</span>
                  <span className="text-green-400">✓ Enabled</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Full System Access</span>
                  <span className="text-green-400">✓ Enabled</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Analytics & Reports</span>
                  <span className="text-green-400">✓ Enabled</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Information */}
      <Card className="bg-gray-900/50 border border-gray-700">
        <CardHeader>
          <CardTitle className="text-xl text-sports-green flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Security Settings
          </CardTitle>
          <p className="text-gray-400">
            Your account security status and recovery information
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
            <h4 className="text-blue-400 font-medium mb-2">🔐 Security Features</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Username & Password Authentication</span>
                <span className="text-green-400">✓ Active</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">PIN Protection</span>
                <span className="text-green-400">✓ Active</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Seed Phrase Recovery</span>
                <span className="text-green-400">✓ Configured</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Account Verification</span>
                <span className={user.account_verified ? 'text-green-400' : 'text-yellow-400'}>
                  {user.account_verified ? '✓ Verified' : '⚠ Pending'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
            <h4 className="text-yellow-400 font-medium mb-2">⚠️ Important Security Notes</h4>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• Keep your seed phrase safe and never share it with anyone</li>
              <li>• Use a strong, unique password for your account</li>
              <li>• Remember your PIN for account recovery</li>
              <li>• Contact support if you suspect unauthorized access</li>
              {user.role === 'admin' && (
                <li className="text-red-400">• As an admin, you have elevated privileges - use them responsibly</li>
              )}
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Account Actions */}
      <Card className="bg-gray-900/50 border border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg text-white">Account Actions</CardTitle>
          <p className="text-gray-400">
            Manage your New School account settings
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => setActiveForm('profile')}
              className="bg-sports-green text-black hover:bg-green-600 font-bold"
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit Profile
            </Button>
            <Button 
              onClick={() => setActiveForm('password')}
              variant="outline" 
              className="border-sports-green text-sports-green hover:bg-sports-green/10"
            >
              Change Password
            </Button>
            <Button 
              onClick={() => setActiveForm('pin')}
              variant="outline" 
              className="border-sports-green text-sports-green hover:bg-sports-green/10"
            >
              Update PIN
            </Button>
            <Button variant="outline" className="border-gray-600 text-gray-400 hover:bg-gray-700">
              Export Account Data
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Note: Some features may be limited for New School accounts and will be expanded in future updates.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default InternalUserProfile;
